import { useState, useEffect, useRef } from "react";
import "./App.css";
import { onMessage } from "firebase/messaging";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "./Components/Sidebar";
import AllTags from "./Tables/AllTags";
import Flanges from "./Tables/Flanges";
import Login from "./Components/Login";
import FlangeForm from "./Forms/FlangeForm";
import FlangeFormNew from "./Forms/FlangeFormNew";
import Certificate from "./Forms/Certificate";
import FlangeHistory from "./View/FlangeHistory";
import FlangeDetails from "./View/FlangeDetail";
import SystemUsers from "./Tables/SystemUsers";
import Reports from "./Tables/Reports";
import EditFlanges from "./Tables/EditFlanges";
import UserForm from "./Forms/UserForm";
import EditUser from "./EditForms/EditUser";
import GroupConfiguration from "./Tables/GroupConfiguration";
import TicketsV2 from "./Tables/TicketsV2";
import TicketDetailV2 from "./Tables/TicketDetailV2";
import TicketDetails from "./Tables/TicketDetails";
import ProjectTable from "./Project/ProjectTable";
import ProjectForm from "./Project/ProjectForm";
import EditProject from "./Project/EditProject";
import FlangeChecklist from "./Components/FlangeChecklist";
import Clients from "./Tables/Clients";
import Equipment from "./Tables/Equipment";
import Loader from "./Components/Loader";
import Departments from "./Tables/Departments";
import Activities from "./Tables/Activities";
import TheRundown from "./Tables/TheRundown";
import Attendance from "./Tables/Attendance";
import AttendanceHeatmap from "./Tables/AttendanceHeatmap";
import EquipmentGroup from "./Tables/EquipmentGroup";
import EquipmentReport from "./Tables/EquipmentReport";
import TBTDailySummary from "./Tables/TBTDailySummary";
import ManpowerDailyReport from "./Tables/ManpowerDailyReport";
import LookAhead from "./Tables/LookAhead";
import EventParticipantsImport from "./Tables/EventParticipantsImport";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
} from "react-router-dom";
import axiosInstance from "./axiosinstance";
import ForgotPassword from "./Components/ForgotPassword";
import EquipmentDetails from "./Tables/EquipmentDetails";
import FieldTechnicianDashboard from "./Components/FieldTechnicianWelcome";
import FlangeDashboard from "./Components/FlangeDashboard";
import ProtectedRoute from "./Components/ProtectedRoute";
import Notifications from "./Tables/Notifications";
import CreateFlangePage from "./Tables/CreateFlangePage";
import TicketNotificationDetails from "./Tables/TicketNotificationDetails";
import GroupChatPage from "./GroupChat/GroupChatPage";
import { getMessagingInstance, registerFcmToken } from "./push/firebaseMessaging";
import NotificationToast from "./Components/NotificationToast";
import "./Components/NotificationToast.css";
import {
  clearAuthStorage,
  getAuthToken,
  hasActiveSession,
  isAuthenticated,
  redirectToLogin,
} from "./utils/authUtils";

const toLower = (value) => String(value || "").trim().toLowerCase();

const AttendanceHeatmapLegacyRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/attendance/heatmap/${encodeURIComponent(projectId || "")}`} replace />;
};

const tryParseJson = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeFcmPayload = (payload) => {
  const data = payload?.data && typeof payload.data === "object" ? payload.data : {};

  const parsedDataPayload =
    tryParseJson(data.payload) ||
    tryParseJson(data.data) ||
    tryParseJson(payload?.payload);

  const mergedData = {
    ...(parsedDataPayload && typeof parsedDataPayload === "object" ? parsedDataPayload : {}),
    ...data,
  };

  const dataType = toLower(
    mergedData.type || mergedData.messageType || mergedData.notification_type,
  );

  const title =
    payload?.notification?.title ||
    mergedData.title ||
    (dataType === "admin_note" || dataType === "equipment" ? "Admin Note Added" : "New Notification");
  const body =
    payload?.notification?.body ||
    mergedData.body ||
    mergedData.message ||
    "";

  return {
    title,
    body,
    data: mergedData,
    dataType,
    rawPayload: payload,
  };
};

const showForegroundBrowserNotification = async ({ title, body, data }) => {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted" ||
    !("serviceWorker" in navigator)
  ) return;

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title || "New Notification", {
      body: body || "",
      icon: "/favicon.png",
      data: data || {},
      tag: data?.notification_id || data?.message_id || undefined,
    });
  } catch (error) {
    // The in-app toast below remains available if the operating system blocks
    // or cannot display a browser notification.
    console.warn("[FCM] Unable to display foreground browser notification.", error);
  }
};

const ProtectedLayout = ({ loggedIn, onSidebarToggle, onLogout, authMeta }) => {
  // Gate on the session (refresh token), NOT the access token. An expired
  // access token is renewed transparently by the axios interceptor, so it must
  // not evict the user here — only a missing/expired refresh token should.
  if (!loggedIn || !hasActiveSession()) {
    clearAuthStorage();
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="protected-app-shell">
      <Sidebar onSidebarToggle={onSidebarToggle} onLogout={onLogout} authMeta={authMeta} />
      <main className="protected-page-scroll-area">
        <Outlet />
      </main>
    </div>
  );
};

const App = () => {
  const [loading, setLoading] = useState(true);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [loggedIn, setLoggedIn] = useState(() => hasActiveSession());
  const [name, setName] = useState("");
  const [authMeta, setAuthMeta] = useState(() => ({
    userId: localStorage.getItem("user_id") || "",
    role: localStorage.getItem("role_name") || "",
    companyId: localStorage.getItem("company_id") || "",
  }));
  // Guards FCM registration to once per authenticated app load (any role).
  // Only flips true on a confirmed (non-null token) registration.
  const fcmRegisteredRef = useRef(false);
  // Prevents overlapping registration attempts while one is in flight, without
  // permanently latching on failure (so a later attempt can still retry).
  const fcmRegisterInFlightRef = useRef(false);

  useEffect(() => {
    const storedName = localStorage.getItem("name");

    if (hasActiveSession()) {
      setLoggedIn(true);
      setName(storedName || "");
      setAuthMeta({
        userId: localStorage.getItem("user_id") || "",
        role: localStorage.getItem("role_name") || "",
        companyId: localStorage.getItem("company_id") || "",
      });
    } else {
      clearAuthStorage();
      setLoggedIn(false);
      setName("");
      setAuthMeta({ userId: "", role: "", companyId: "" });
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  const handleSidebarToggle = (expanded) => {
    setIsSidebarExpanded(expanded);
  };

  const handleLogin = (authKey, refresh_token, email, userId, role_name) => {
    if (authKey && refresh_token && email && userId && role_name && isAuthenticated()) {
      setLoggedIn(true);
      setName(email);
      setAuthMeta({
        userId: String(userId || ""),
        role: String(role_name || ""),
        companyId: localStorage.getItem("company_id") || "",
      });
    }
  };

  const handleLogout = async () => {
    const refresh_token = localStorage.getItem("refresh_token");
    const fcm_token = localStorage.getItem("fcm_token");

    try {
      if (refresh_token) {
        await axiosInstance.post("/api/logout", {
          refresh_token,
          ...(fcm_token && { fcm_token }),
        });
      }
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      clearAuthStorage();
      setLoggedIn(false);
      setName("");
      setAuthMeta({ userId: "", role: "", companyId: "" });
      redirectToLogin();
    }
  };

  // Global, role-agnostic FCM registration. Runs once per authenticated app
  // load for every user that reaches the authenticated app — not tied to any
  // single page/component. Resets on logout so the next login re-registers
  // (which also lets the backend reactivate an inactive token).
  useEffect(() => {
    if (!loggedIn) {
      // Reset on logout so the next login can register again (and let the
      // backend reactivate an inactive token).
      fcmRegisteredRef.current = false;
      fcmRegisterInFlightRef.current = false;
      return undefined;
    }

    if (fcmRegisteredRef.current) {
      console.info("[FCM] Registration skipped; already registered this session.");
      return undefined;
    }

    // An attempt is already running — don't start a duplicate.
    if (fcmRegisterInFlightRef.current) return undefined;

    const authKey = getAuthToken();
    if (!authKey) return undefined;

    // Only auto-register silently when the user has ALREADY granted permission.
    // When permission is "default" (never asked), we must NOT trigger the browser
    // prompt automatically — it would appear with no context. The user opts in
    // explicitly via the header "Enable alerts" affordance, which shows an
    // in-app explainer first. (UX Gap 2)
    if (
      typeof Notification !== "undefined" &&
      Notification.permission !== "granted"
    ) {
      console.info(
        "[FCM] Permission not yet granted; awaiting explicit user opt-in.",
      );
      return undefined;
    }

    let cancelled = false;
    fcmRegisterInFlightRef.current = true;
    console.info("[FCM] Registration started.");

    registerFcmToken(authKey)
      .then((token) => {
        if (cancelled) return;
        if (token) {
          // Only latch the guard on a confirmed registration.
          fcmRegisteredRef.current = true;
          console.info("[FCM] Registration success; token registered.");
        } else {
          // Permission default/denied, auth not ready, SW delay, getToken
          // failure, or network error — keep the guard open so a later
          // attempt can retry within this session.
          fcmRegisteredRef.current = false;
          console.warn("[FCM] Registration failed; will allow retry later.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Defensive: registerFcmToken already fails safely to null, but never
        // latch the guard if it somehow throws.
        fcmRegisteredRef.current = false;
        console.warn("[FCM] Registration failed; will allow retry later.", err);
      })
      .finally(() => {
        if (!cancelled) fcmRegisterInFlightRef.current = false;
      });

    return () => {
      cancelled = true;
      // Clear the in-flight guard on cleanup too. The .finally() above is
      // skipped when cancelled, so without this the flag would latch true
      // forever and block every future registration attempt (e.g. after a
      // fast logout→login or a StrictMode remount).
      fcmRegisterInFlightRef.current = false;
    };
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return undefined;

    let unsubscribe = null;
    let cancelled = false;

    // Resolve messaging lazily; on unsupported browsers it returns null and we
    // simply skip attaching the foreground listener (no crash).
    getMessagingInstance().then((messaging) => {
      if (cancelled || !messaging) return;

      unsubscribe = onMessage(messaging, (payload) => {
      console.log("Foreground FCM message:", payload);

      const normalizedPayload = normalizeFcmPayload(payload);
      const { title, body, data, dataType } = normalizedPayload;
      const normalizedTitle = String(title || "").trim().toLowerCase();

      if (dataType === "process_update") {
        window.dispatchEvent(
          new CustomEvent("process-update-received", {
            detail: {
              equipment_id: data.equipment_id,
              process_id: data.process_id,
              status: data.status,
              action: data.action,
            },
          }),
        );
        return;
      }

      if (
        dataType === "equipment_on_hold" ||
        dataType === "equipment_hold_released"
      ) {
        window.dispatchEvent(
          new CustomEvent("equipment-hold-updated", {
            detail: {
              equipment_id: data.equipment_id || data.reference_id,
              is_on_hold: dataType === "equipment_on_hold",
              hold_reason: data.hold_reason || "",
              held_by: data.held_by || null,
              held_at: data.held_at || null,
              type: dataType,
            },
          }),
        );
      }

      // Ticket activity (comment/mention/status/assignment). Let an open ticket
      // page refresh itself. No early return: this is a user-facing push, so the
      // toast + notification handling below should still run.
      if (dataType === "v2_ticket") {
        window.dispatchEvent(
          new CustomEvent("ticket-update-received", {
            detail: { ticket_id: data.ticket_id, event: data.event },
          }),
        );
      }

      const isProcessReady =
        dataType === "process_ready" ||
        normalizedTitle === "process ready" ||
        normalizedTitle === "processes ready";

      if (isProcessReady) {
        window.dispatchEvent(
          new CustomEvent("process-ready-received", {
            detail: data || {},
          }),
        );
      }

      // Step 2: a process whose finish-constraints are now met (ready to End).
      const isReadyToFinish =
        dataType === "process_ready_to_end" ||
        normalizedTitle === "ready to finish";

      if (isReadyToFinish) {
        window.dispatchEvent(
          new CustomEvent("process-ready-to-finish-received", {
            detail: data || {},
          }),
        );
      }

      toast.success(
        <NotificationToast
          title={title}
          body={body}
          data={payload?.data || {}}
          isProcessReady={isProcessReady}
        />,
        {
          containerId: "fcm-global-toast",
          toastId: payload?.messageId || data?.notification_id || `${title}:${body}`,
          icon: false,
          closeButton: true,
          className: "custom-notification-toast",
          bodyClassName: "custom-notification-toast-body",
        },
      );

      // Firebase does not display notification messages automatically while
      // the page is focused. Show it explicitly through the registered worker;
      // keep the toast as the in-app presentation and fallback.
      showForegroundBrowserNotification({ title, body, data });

      window.dispatchEvent(
        new CustomEvent("foreground-notification-received", {
          detail: normalizedPayload,
        }),
      );
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [loggedIn]);

  if (loading) {
    return (
      <div style={{ display: "flex", height: "100vh", justifyContent: "center", alignItems: "center" }}>
        <div style={{ transform: "scale(2)" }}>
          <Loader />
        </div>
      </div>
    );
  }

  const authOk = hasActiveSession();
  const role = localStorage.getItem("role_name")?.toLowerCase();
  const authenticatedHome = role === "field technician" || role === "admin" ? "/welcome-page" : "/Projects";

  return (
    <Router>
      <ToastContainer
        containerId="fcm-global-toast"
        position="top-right"
        autoClose={4000}
        theme="light"
      />
      <Routes>
        <Route path="/" element={<Navigate to={loggedIn && authOk ? authenticatedHome : "/login"} replace />} />

        {/* Public auth routes (no sidebar/header shell) */}
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/ForgotPassword" element={<Navigate to="/forgot-password" replace />} />
        <Route path="/view-tag-info/:flange_id" element={<FlangeDetails />} />

        {/* Protected app routes rendered inside authenticated shell */}
        <Route
          element={
            <ProtectedLayout
              loggedIn={loggedIn}
              onSidebarToggle={handleSidebarToggle}
              onLogout={handleLogout}
              authMeta={authMeta}
            />
          }
        >
          <Route path="/Import-Flanges" element={<AllTags isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/flanges" element={<Flanges isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/all-flanges" element={<Flanges isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/All-Flanges" element={<Flanges isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/create-flange" element={<CreateFlangePage isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/EditFlanges/:flange_id" element={<EditFlanges isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/system-users" element={<SystemUsers isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/Group-Assignments" element={<GroupConfiguration isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/tickets" element={<TicketsV2 isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/tickets/:ticketId" element={<TicketDetailV2 isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/task-list" element={<Activities isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/the-rundown" element={<TheRundown isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/attendance" element={<Attendance isSidebarExpanded={isSidebarExpanded} />} />
          <Route
            path="/attendance/heatmap/:projectId"
            element={
              <ProtectedRoute
                allowedRoles={["admin", "administrator"]}
                requiredPermission="attendance_heatmap_view"
              >
                <AttendanceHeatmap isSidebarExpanded={isSidebarExpanded} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:projectId/attendance/heatmap"
            element={<AttendanceHeatmapLegacyRedirect />}
          />
          <Route path="/my-activity" element={<Navigate to="/task-list" replace />} />
          <Route path="/notifications" element={<Notifications isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/group-chat" element={<GroupChatPage isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/ticket-notification-details" element={<TicketNotificationDetails isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/TicketDetails/:ticketId" element={<TicketDetails isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/user-form" element={<UserForm isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/Edit-user-form/:user_id" element={<EditUser isSidebarExpanded={isSidebarExpanded} />} />

          <Route
            path="/Edit-project-form/:project_id"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                <EditProject isSidebarExpanded={isSidebarExpanded} />
              </ProtectedRoute>
            }
          />

          <Route path="/welcome-page" element={<Navigate to="/equipment-dashboard" replace />} />
          <Route path="/dashboard" element={<Navigate to="/equipment-dashboard" replace />} />
          <Route path="/equipment-dashboard" element={<FieldTechnicianDashboard isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/flange-dashboard" element={<FlangeDashboard isSidebarExpanded={isSidebarExpanded} />} />

          <Route
            path="/Projects"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                <ProjectTable isSidebarExpanded={isSidebarExpanded} />
              </ProtectedRoute>
            }
          />

          <Route path="/flange-report" element={<Reports isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/flange-reports" element={<Navigate to="/flange-report" replace />} />
          <Route path="/reports" element={<Navigate to="/flange-report" replace />} />
          <Route path="/Reports" element={<Navigate to="/flange-report" replace />} />

          <Route
            path="/project-form"
            element={
              <ProtectedRoute allowedRoles={["admin", "manager", "supervisor"]}>
                <ProjectForm isSidebarExpanded={isSidebarExpanded} />
              </ProtectedRoute>
            }
          />

          <Route path="/clients" element={<Clients isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/equipments" element={<Equipment isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/departments" element={<Departments isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/equipment-group" element={<EquipmentGroup isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/equipment-report" element={<EquipmentReport isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/safety-daily-summary" element={<TBTDailySummary isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/tbt-daily-summary" element={<Navigate to="/safety-daily-summary" replace />} />
          <Route path="/manpower-report" element={<ManpowerDailyReport isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/manpower-daily-report" element={<ManpowerDailyReport isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/look-ahead" element={<LookAhead isSidebarExpanded={isSidebarExpanded} />} />
          {[
            "/import-participants",
            "/event-participants",
            "/Event-Participants",
            "/EventParticipants",
            "/event_participants",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute allowedRoles={["admin", "administrator"]}>
                  <EventParticipantsImport isSidebarExpanded={isSidebarExpanded} />
                </ProtectedRoute>
              }
            />
          ))}
          <Route path="/lookahead" element={<Navigate to="/look-ahead" replace />} />
          <Route path="/equipment-details/:equipment_id" element={<EquipmentDetails isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/flange-Forms/:flangeId" element={<FlangeForm isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/Flange_history/:flange_id" element={<FlangeHistory isSidebarExpanded={isSidebarExpanded} />} />

          <Route path="/flange-checklist" element={<FlangeChecklist isSidebarExpanded={isSidebarExpanded} />} />
          <Route path="/flange-Forms-new/:flangeId" element={<FlangeFormNew />} />
          <Route path="/certificate" element={<Certificate />} />
        </Route>

        <Route path="*" element={<Navigate to={loggedIn && authOk ? authenticatedHome : "/login"} replace />} />
      </Routes>
    </Router>
  );
};

export default App;
